<template>
  <Card
    :additional-classes="cardAdditionalClasses"
    @click="handleOpenEditPanel(traveller)"
  >
    <section
      class="paticipant__full-name__edit-button d-flex justify-content-between align-items-center"
    >
      <div class="paticipant__full-name d-flex d-flex gap-1 align-items-center">
        <div
          class="paticipant__first-name text-capitalize"
          :class="nameTextClasses"
        >
          {{ firstName }}
        </div>
        <div
          class="paticipant__last-name text-capitalize"
          :class="nameTextClasses"
        >
          {{ lastName }}
        </div>
        <CheckMark v-if="dataFulfilled" />
        <!-- Age is always shown, but behaves differently based on panelMode -->
        <div class="paticipant__age" :class="ageDisplayClass">
          {{ age }}
        </div>
      </div>

      <Button v-if="panelMode" :classes="['btn']">
        <template #svg><ArrowRightIcon /></template>
      </Button>
    </section>
    <EdittravellerPanel
      v-if="selectedTraveller"
      :selectedTraveller="selectedTraveller"
      :panelTitle="`${firstName} ${lastName}`"
      :panelId="panelId"
    />
  </Card>
</template>

<script lang="ts" setup>
  // Vue
  import { computed, ComputedRef, defineProps, Ref, ref } from 'vue'

  // Types
  import { TravellerSummary } from '@/services/BookingService.interface'

  // Config
  import { ModalId } from '@/config/modalIds'

  // Icons
  import ArrowRightIcon from '@/assets/ArrowRightIcon.vue'

  // Utils
  import { getAge } from '@/utils'

  // Components
  import Card from '@/components/UI/Card.vue'
  import Button from '@/components/UI/Button.vue'
  import CheckMark from './CheckMark.vue'
  import EdittravellerPanel from './EditTravellerPanel.vue'

  // Translations
  import { useI18n } from 'vue-i18n'
  const { t } = useI18n()

  // Store
  import { useStore } from '@/store'
  const store = useStore()

  // Props
  interface Props {
    traveller: TravellerSummary
    travellerIndex: number
    departureDate?: string
    panelMode?: boolean
  }

  const props = defineProps<Props>()

  // Refs
  const selectedTraveller: Ref<TravellerSummary | null> = ref(null)

  // Methods
  const handleOpenEditPanel = (traveller: TravellerSummary): void => {
    if (!props.panelMode) return
    selectedTraveller.value = traveller
    store.commit('modal/open', panelId.value)
  }

  const truncateText = (text: string, length = 28) => {
    return text.length > length ? text.substring(0, length) + '...' : text
  }

  const getDisplayName = (name: string | null, fallback: string): string => {
    if (!name) {
      return fallback
    }
    return name
  }

  const ageDisplayClass = computed(() => {
    return props.panelMode ? 'd-block' : 'd-inline'
  })

  // Computed
  const age: ComputedRef<string> = computed(() => {
    const computedAge: string = getAge(
      props.traveller.birthday,
      props.departureDate
    )
    const ageString = `${computedAge} ${t('travellers.yearsOld')}`
    return props.panelMode ? ageString : `(${ageString})` // wrapper in parentheses if not in panel
  })

  const firstName: ComputedRef<string> = computed(() => {
    return truncateText(
      getDisplayName(
        props.traveller?.firstName,
        props.panelMode ? t('travellers.traveller') : t('travellers.firstName')
      )
    )
  })

  const lastName: ComputedRef<string> = computed(() => {
    return truncateText(
      getDisplayName(
        props.traveller?.lastName,
        props.panelMode
          ? (props.travellerIndex + 1).toString()
          : t('travellers.lastName')
      )
    )
  })

  const panelId: ComputedRef<string> = computed(() => {
    return `${ModalId.EDIT_TRAVELLER_PANEL}-${selectedTraveller.value?.id}`
  })

  const dataFulfilled: ComputedRef<boolean> = computed(() => {
    return !!(props.traveller.firstName && props.traveller.lastName)
  })

  // CSS classes
  const nameTextClasses: ComputedRef<string> = computed(() => {
    const baseClass = 'text-capitalize'

    const textClasses = props.panelMode ? 'heading' : 'body'
    return `${baseClass} ${textClasses}`
  })

  const cardAdditionalClasses: ComputedRef<string> = computed(() => {
    const baseClasses = 'traveller  d-flex shadow-none padding-none gap-1'

    const additionalClasses = props.panelMode
      ? 'cursor-pointer flex-column  px-3'
      : 'py-none border-0 flex-row'
    return `${baseClasses} ${additionalClasses}`
  })
</script>

<style scoped>
  .traveller {
    flex-wrap: wrap; /* Allow wrapping if needed */
  }

  .paticipant__full-name {
    flex-wrap: wrap; /* Ensure name and age can wrap */
  }
</style>
